<template>
  <div>
  <multi-select
    v-if="optionsToComponent && optionLabelToComponent"
    :key="optionLabelToComponent"
    :id='name'
    ref="select"
    v-model="inputValue"
    :options="optionsToComponent"
    :show-labels="false"
    :label="optionLabel"
    :custom-label="customLabel"
    :value="optionValue"
    :track-by="optionValue"
    :group-values="groupValues"
    :group-label="groupLabel"
    :group-select="groupSelect"
    :showNoResults="showNoResults"
    :placeholder="placeholderLabel"
    :searchable="searchable"
    :multiple="multiple"
    :max="max"
    :close-on-select="closeOnSelect"
    :allowEmpty="allowEmpty"
    :preselectFirst="preselectFirst"
    :taggable="taggable"
    :tagPlaceholder="tagPlaceholderLabel"
    :loading="loading"
    :preserveSearch="preserveSearch"
    :resetAfter="resetAfter"
    :clearOnSelect="clearOnSelect"
    :open-direction="dropdownDirection"
    :hideSelected="hideSelected"
    :disabled="disabled"
    :class="[
      classNames,
      { multiselect__tags_white: whiteBackground, loading: loading },
      'actions-' + enabledActionsLength
    ]"
    :internal-search="internalSearch"
    class="input-select"
    @tag="onAddTag"
    @input="onSelectChange"
    @open="onOpenInput"
    @close="onMultiselectClose"
    @remove="onRemoveOption"
    @search-change="onSearchChangeInput"
    @keydown="onKeyDown"
  >
    <template slot="option" scope="props">
      <slot v-if="hasSlot" name="custom" :scope="props"></slot>
      <div v-else class="option">
        {{(displayLabel(props.option))}}
      </div>
    </template>

    <template slot="beforeList">
      <slot name="beforeListCustom"></slot>
    </template>

    <template
      v-if="inputValue && inputValue[optionLabel]"
      slot="singleLabel"
    >
      {{ inputValue[optionLabel] }}
    </template>

    <template slot="noResult">
      <slot name="noResult"></slot>
    </template>

    <template slot="noOptions">
      <slot name="noOptions"></slot>
    </template>

    <!-- afterList is rendered inside <ul class="multiselect__content">; use <li> like vue-multiselect option rows (invalid <div> siblings are unreliable). -->
    <template slot="afterList">
      <li
        v-if="optionsFooterLoading"
        class="multiselect__element input-select__options-footer-loading"
        role="status"
        aria-live="polite"
      >
        <span class="multiselect__option input-select__options-footer-loading-inner">
          <i class="fas fa-spinner rotate" aria-hidden="true"></i>
        </span>
      </li>
      <slot name="afterList" />
    </template>
    
    <div class="input-actions" slot="clear">
      <span v-if="canSaveOptions"
        class="lock-selected"
        v-tooltip="$t('form.tooltip.save_selection')"
        @mousedown.prevent.stop="saveSelection"
      >
        <i class="far fa-bookmark"></i>
      </span>

      <span v-if="canSelectAll"
        class="select-all"
        v-tooltip="selectAllTooltipMessage"
        @mousedown.prevent.stop="selectAllOptions"
      >
       <span class="select-all-icons">
          <i class="far fa-square"/>
          <i class="fas fa-square fa-inverse"/>
          <i class="far fa-check-square"/>
        </span>
      </span>

      <span v-if="canClearSelect"
        class="clear-select"
        v-tooltip="clearAllTooltipMessage"
        @mousedown.prevent.stop="clearSelectValue"
      >
        <i class="fas fa-times"></i>
      </span>
    </div>

    <span v-if="customCaretClass"
      class="custom-caret"
      :class="customCaretClass"
      slot="caret"
      slot-scope="{ toggle }"
      @mousedown.prevent.stop="toggle"
    />

    
    
    
  </multi-select>
  <slot name="importexport"/>
  </div>
</template>

<script>
import MultiSelect from 'vue-multiselect';
import _ from 'lodash';

export default {
  props: {
    name: String,
    options: {
      type: Array,
      default: () => []
    },
    onChange: Function,
    clearSelectedValue: Function,
    clearAllTooltipMessage: String,
    selectAllAvailableOptions: Function,
    selectAllTooltipMessage: String,
    disableSelectAll: {
      type: Boolean,
      default: false
    },
    max: {
      type: Number,
      default: undefined
    },
    selectedValue: [Number, String, Object, Array, Boolean],
    value: {
      type: [Number, String, Object, Array, Boolean],
      required: false,
      default: undefined
    },
    optionValue: {
      type: String,
      required: false,
      default: 'value'
    },
    optionLabel: {
      type: String,
      required: false,
      default: 'label'
    },
    customLabel: {
      type: Function,
      required: false
    },
    groupValues: {
      type: String,
      required: false,
      default: ''
    },
    groupLabel: {
      type: String,
      required: false,
      default: ''
    },
    groupSelect: Boolean,
    isGroupOption: Boolean,
    loading: {
      type: Boolean,
      default: false
    },
    
     // When true, shows a small spinner row at the bottom of the open options list (e.g. paginated load-more).
    optionsFooterLoading: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      required: false,
      default: false
    },
    searchableProps: {
      type: Array,
      required: false,
      default: () => {
        return [];
      }
    },
    showNoResults: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    },
    translatePlaceholder: {
      type: String,
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    classNames: {
      type: String,
      default: ''
    },
    whiteBackground: {
      type: Boolean,
      required: false,
      default: false,
    },
    customCaretClass: {
      type: String,
      default: null
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    allowEmpty: {
      type: Boolean,
      required: false,
      default: true
    },
    preselectFirst: {
      type: Boolean,
      default: false
    },
    taggable: {
      type: Boolean,
      default: false
    },
    onTag: {
      type: Function,
      default: () => null
    },
    hasCustomTagFields: {
      type: Boolean,
      default: () => false
    },
    tagPlaceholder: {
      type: String
    },
    translateTagPlaceholder: {
      type: String
    },
    onOpen: {
      type: Function,
      default: () => null
    },
    onSearchChange: {
      type: Function,
      default: () => null
    },
    
     // Called when the options list is scrolled to the end (for async / paged option sources).
    onOptionsScrollEnd: {
      type: Function,
      default: null
    },
    internalSearch: {
      type: Boolean,
      default: true
    },
    preserveSearch: {
      type: Boolean,
      default: false
    },
    resetAfter: {
      type: Boolean,
      default: false
    },
    clearOnSelect: {
      type: Boolean,
      default: true
    },
    dropdownDirection: {
      type: String,
      default: '',
      validator: function (value) {
        return ['', 'bottom', 'below', 'above', 'top'].indexOf(value) !== -1
      }
    },
    hideSelected: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isTaggingAllowed: {
      type: Function
    },
    disableWhiteSpace: {
      type: Boolean,
      default: false
    },
    lowerCase: {
      type: Boolean,
      default: false
    }
  },

  components: {
    MultiSelect
  },

  computed: {
    searchablePropsJoin() {
      if (this.searchableProps && this.searchableProps.length)
      return this.searchableProps.join('_');
      
      return '';
    },
    searchablePropsAdditional() {
      if (!this.searchableProps.length) return [];

      let newArray = _.cloneDeep(this.searchableProps);
      newArray.splice(0, 1);

      return newArray;
    },
    optionLabelToComponent() {
      if (!this.searchableProps.length) return this.optionLabel;

      return this.searchablePropsJoin;
    },
    optionsToComponent() {
      let options = [];

      if(this.isGroupOption) return this.options;

      if (!this.searchableProps || !this.searchableProps.length)  {
        options = this.options;
      } else {
        options = this.options.map(option => {
          let labelPropValue = '';
          
          this.searchableProps.forEach(prop => {
            labelPropValue += ` ${option[prop]}`
          });

          return { ...option, [this.searchablePropsJoin]: labelPropValue }
        });
      }

      return options.filter(option => option[this.optionLabel]);
    },
    hasSlot: function() {
      return !!this.$scopedSlots.custom;
    },
    selectOptions: function() {
      return (this.loading === false && this.options) || [];
    },
    fixedPosition: function () {
      if (
        this.$refs &&
        this.$refs.select &&
        this.$refs.select.$parent &&
        this.$refs.select.$parent.$el &&
        this.$refs.select.$parent.$el.classList.contains("datatable-input-select--fixed")
      )
        return true;

      return false;
    },
    inputValue: {
      get() {
        if (this.value === undefined) return this.selected;
        if(this.isGroupOption) {
          // Sync when value is set externally; skip when value is still null while selected
          // is being updated from a click (otherwise the first selection is cleared).
          if (
            this.value !== null &&
            this.value !== undefined &&
            this.selected &&
            typeof this.selected === 'object' &&
            this.selected[this.optionValue] !== this.value
          ) {
            const target = this.findGroupOptionByValue(this.value);
            this.$emit('change', target);
            return target;
          }
          return this.selected;
        }

        return this.getOptionByKey(this.value);
      },
      set(newValue) {
        this.selected = newValue;

        let valueToSend;

        if (_.isArray(newValue)) {
          valueToSend = newValue.map(item => {
            return item[this.optionValue];
          });
        } else {
          valueToSend = newValue ? newValue[this.optionValue] : null;
        }
        this.$emit('input', valueToSend);
      }
    },
    placeholderLabel () {
      return this.translatePlaceholder ? this.$i18n.t(this.translatePlaceholder) : this.placeholder
    },
    tagPlaceholderLabel () {
      return this.translateTagPlaceholder ? this.$i18n.t(this.translateTagPlaceholder) : this.tagPlaceholder || undefined
    },
    tableWrapper () {
      return this.$refs.select.$el.closest(".table-wrapper--overflow");
    },

    canClearSelect () {
      return !this.disabled && this.allowEmpty && typeof this.clearSelectedValue === 'function';
    },

    canSelectAll () {
      return this.multiple && !this.disabled && this.disableSelectAll !== true && typeof this.selectAllAvailableOptions === 'function';
    },

    canSaveOptions () {
      return 'saveSelection' in this.$listeners;
    },

    enabledActionsLength () {
      return [this.canClearSelect, this.canSelectAll, this.canSaveOptions].filter(v => v).length;
    }
  },

  data () {
    return {
      selected: this.updateSelected(),
      allOptions: [],
      searchTimeout: null,
      // HTMLElement
      optionsListScrollEl: null,
      optionsListScrollHandler: null
    }
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    if (this.fixedPosition && this.tableWrapper) {
      this.tableWrapper.addEventListener('scroll', this.handleScroll);
    }
    
    this.$nextTick(() => {
      _.has(this.$refs, 'select.$refs.search') && this.$refs.select.$refs.search.setAttribute("autocomplete", "off")
    })

    this.allOptions = this.options;

    // Grouped values are resolved by updateSelected (array -> flat option
    // objects). Do not wrap that result again for multiple — findGroupOptionByValue
    // already returns an array when value is an array.
    if (this.isGroupOption && this.value !== null && this.value !== undefined) {
      const match = this.updateSelected(this.value);
      if (match !== null && match !== undefined) {
        this.selected = match;
      }
    }

    const inputField = this.$refs.select && this.$refs.select.$el.querySelector('input[type="text"]');
    if (inputField) {
      inputField.addEventListener('keydown', this.onKeyDown);
    }
    this.optionsListScrollHandler = this.handleOptionsListScroll.bind(this);
  },

  beforeDestroy() {
    this.detachOptionsListScrollListener();
    window.removeEventListener('scroll', this.handleScroll);
    if (this.tableWrapper) {
      this.tableWrapper.removeEventListener('scroll', this.handleScroll);
    }
    
    const inputField = this.$refs.select && this.$refs.select.$el.querySelector('input[type="text"]');
    if (inputField) {
      inputField.removeEventListener('keydown', this.onKeyDown);
    }
  },

  watch: {
    selectedValue: {
      handler: function(value) {
        this.selected = this.updateSelected();
      }
    },
    value: {
      handler: function(value) {
        this.selected = this.updateSelected(value);
      }
    },
    options(newValue) {
      this.allOptions = [...this.options, newValue];

      if (this.isGroupOption && this.value !== null && this.value !== undefined) {
        const updated = this.updateSelected(this.value);
        if (updated) {
          this.selected = updated;
        }
      }
    }
  },

  methods: {
    saveSelection() {
      this.$emit('saveSelection', this.inputValue);
    },
    clearSelectValue() {
      _.isArray(this.inputValue) ? this.inputValue = []: this.inputValue = undefined;
      if (this.clearSelectedValue && typeof this.clearSelectedValue === 'function') {
        this.clearSelectedValue(this.inputValue)
      }
      this.$emit('clearSelectValue')
    },
    selectAllOptions() {
      this.inputValue = this.options.map(o => ({value: o.value, label: o.label}) );
      if (typeof this.max === 'number' && Array.isArray(this.inputValue) && this.inputValue.length > this.max) {
        this.inputValue = this.inputValue.slice(0, this.max);
      }
      if (this.selectAllAvailableOptions && typeof this.selectAllAvailableOptions === 'function') {
        this.selectAllAvailableOptions(this.inputValue)
      }
    },
    getOptionByKey(value) {
      if (_.isArray(value)) return this.getOptionArrayByKey(value);

      return this.options.find((option) => {
        return _.isEqual(option[this.optionValue], value);
      });
    },

    findGroupOptionByValue(value) {
      if (value === null || value === undefined) {
        return null;
      }

      if (_.isArray(value)) {
        return value
          .map((item) => this.findGroupOptionByValue(item))
          .filter((item) => item !== null);
      }

      for (const group of this.options) {
        const groupOptions = group[this.groupValues];
        if (!groupOptions || !groupOptions.length) {
          continue;
        }

        const match = groupOptions.find(
          (option) => option[this.optionValue] === value
        );
        if (match) {
          return match;
        }
      }

      return null;
    },

    getGroupOptionsByKey(values) {
      if(Array.isArray(values)) {
        return values.map(value => {
          return this.options.find(option => {
            return _.isEqual(option[this.optionValue], value);
          });
        });
      } else {
        return this.options.find(option => {
          return _.isEqual(option[this.optionValue], values);
        });
      }
    },

    displayLabel(option) {

      if(this.isGroupOption) {
        return option.$groupLabel || option[this.optionLabel];
      }

      let label;
      if (this.customLabel) {
        label = this.customLabel(option);
      }

      if (typeof label == "undefined" || label == "undefined") {
        label = option[this.optionLabel];
      }

      return label;
    },

    getOptionArrayByKey(value) {
      const self = this;
      return this.allOptions.filter((option) => {
        return value.find(val => _.isEqual(option[this.optionValue], val));
      });
    },

    onOpenInput: function() {
      if (this.fixedPosition) {
        this.setPosition();
      }

      this.onOpen();
      this.$nextTick(() => {
        this.attachOptionsListScrollListener();
      });
    },

    
    // Detaches scroll listener when the multiselect dropdown closes.
    onMultiselectClose: function() {
      this.detachOptionsListScrollListener();
    },

    // Binds scroll listener to the multiselect options panel when `onOptionsScrollEnd` is provided.
    attachOptionsListScrollListener: function() {
      this.detachOptionsListScrollListener();
      if (typeof this.onOptionsScrollEnd !== 'function' || !this.$refs.select || !this.$refs.select.$el) {
        return;
      }
      const el = this.$refs.select.$el.querySelector('.multiselect__content-wrapper');
      if (!el) {
        return;
      }
      this.optionsListScrollEl = el;
      el.addEventListener('scroll', this.optionsListScrollHandler, { passive: true });
    },

    // Removes the options-list scroll listener if present.
    detachOptionsListScrollListener: function() {
      if (this.optionsListScrollEl && this.optionsListScrollHandler) {
        this.optionsListScrollEl.removeEventListener('scroll', this.optionsListScrollHandler);
      }
      this.optionsListScrollEl = null;
    },

    // Invokes onOptionsScrollEnd when the user scrolls near the bottom of the options list.
    handleOptionsListScroll: function() {
      if (typeof this.onOptionsScrollEnd !== 'function' || !this.optionsListScrollEl) {
        return;
      }
      const el = this.optionsListScrollEl;
      const thresholdPx = 48;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx) {
        this.onOptionsScrollEnd();
      }
    },

    onSearchChangeInput: function (value = "") {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout( ()=>{
        if (this.fixedPosition) {
          this.setPosition();
        }
        if(value){
          this.onSearchChange(value);
          this.$emit('onSearchChangeInput', value)
        }
      }, 500)
    },

    setPosition: function() {
      const input = this.$refs.select.$el;
      const optionsWrapper = input.querySelector('.multiselect__content-wrapper')
      const { top, left, bottom, width } = input.getBoundingClientRect();

      optionsWrapper.style.width = width + 'px';

      setTimeout(function() {
        if (input.classList.contains('multiselect--above')) {
          const wrapperHeight = optionsWrapper.getBoundingClientRect().height;
          optionsWrapper.style.bottom = 'unset';
          optionsWrapper.style.top = (top - wrapperHeight) + 'px';
          optionsWrapper.style.left = left + 'px';
        }
        else {
          optionsWrapper.style.top = bottom + 'px';
          optionsWrapper.style.left = left + 'px';
        }
      }, 10);
    },

    onAddTag: function(value) {
      if (this.disableWhiteSpace) {
        value = value.replace(/\s+/g, '');
      }

      if (this.lowerCase) {
        value = value.toLowerCase();
      }

      if (typeof this.isTaggingAllowed === 'function' && !this.isTaggingAllowed(value)) {
        return;
      }
      
      const option = { 
        [this.optionLabel]: value, 
        [this.optionValue]: value 
      };

      if (this.hasCustomTagFields) {
        this.onTag(option);
      } else {
        this.onTag(option);
        this.selectOptions.push(option);
        if(this.multiple) {
          if (!_.isArray(this.selected)) this.selected = []; 
          this.selected.push(option);
        }
        else {
          this.selected = option;
        }
        this.onSelectChange(this.selected);
      }      
    },

    onRemoveOption(removedOption, id) {
      let selected;

      if (!_.isArray(this.inputValue)) {
        selected = null;
      } else {
        selected = this.inputValue.filter(item => {
          return item[this.optionValue] !== removedOption[this.optionValue];
        });
      }

      this.onSelectChange(selected, true);
    },

    updateSelected(value) {
      const resolvedValue = value !== undefined
        ? value
        : (this.value !== undefined ? this.value : this.selectedValue);

      if (_.isArray(resolvedValue)) {
        if (this.isGroupOption) {
          return resolvedValue
            .map((item) => this.findGroupOptionByValue(item))
            .filter((item) => item !== null);
        }

        return this.options.filter((opt) =>
          resolvedValue.find((sel) => _.isEqual(sel, opt[this.optionValue]))
        );
      }

      if (resolvedValue === undefined || resolvedValue === null) {
        return null;
      }

      if (this.isGroupOption) {
        return this.findGroupOptionByValue(resolvedValue);
      }

      return this.options.find((opt) =>
        _.isEqual(opt[this.optionValue], resolvedValue)
      ) || null;
    },

    onSelectChange (value, isRemove = false) {
      let val;

      if (
        (_.isArray(value) && !this.multiple) ||
        (_.isArray(value) && this.multiple && isRemove)
      ) {
        val = value.map((opt) => opt[this.optionValue]);
      } else if (_.isArray(value) && this.multiple && !isRemove) {
        const newVal = value.map((opt) => opt[this.optionValue]);
        const currentVal = this.inputValue.map((opt) => opt[this.optionValue]);
        val = _.uniq([...newVal, ...currentVal]);
      } else {
        val = value ? value[this.optionValue] : null;
      }
      if (this.multiple === true && typeof this.max === 'number' && Array.isArray(val) && val.length > this.max) {
        val = val.slice(0, this.max);
        this.inputValue = this.options.filter((opt) => val.includes(opt[this.optionValue]));
      }
      if (this.onChange && typeof this.onChange === 'function') {
        this.onChange(val);
      }
      this.$emit('change', val)

      // if multiple don't lose the focus and close the dropdown
      // allow user to select multiple options
      // if(this.multiple) {
      //   this.$refs.select.$el.focus();
      // }
    },

    handleScroll: function() {
      if (this.fixedPosition === true) {
        this.$refs['select'].deactivate();
      }
    },

    onKeyDown (e) {
      if (this.disableWhiteSpace && e.key === ' ') {
        e.preventDefault();
      }
    }
  }
}
</script>

<style lang="scss">
  .select-props-additional {
    margin-left: 5px;
    color: $vis-axis-color;
  }

  .fa-language {
      font-size: 1.6rem;

      + .clear-select {
        margin-right: 15px;
      }
  }

  .clear-select,
  .select-all {
    position: absolute;
    top: 4px;
    right: 30px;
    opacity: 0.6;
    &:hover {
      opacity: 1;
    }
  }

  .clear-select + .select-all {
    right: 50px;
  }

  .select-all {
    font-size: 0.8rem;
    z-index: 10;
  }

  .select-all-icons {
    position: relative;
    top: -2px;
    i {
      &.fas.fa-square.fa-inverse {top: 6px; right: 2px; position: absolute;}
      &.far.fa-check-square      {top: 7px; right: 3px; position: absolute;}
    }
  }

  .input-select {

    .input-select__options-footer-loading {
      pointer-events: none;
      .input-select__options-footer-loading-inner {
        display: block;
        text-align: center;
        justify-content: center;
        color: $vis-primary-color;
        cursor: default;
        white-space: normal;
      }
    }

    .input-actions {
      position: absolute;
      top: 5px;
      right: 30px;
      display: flex;
      align-items: center;
      gap: 10px;

      & > span {
        opacity: 0.6;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 20px;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;

        &:hover {
          opacity: 1;
        }
      }
    }

    &.actions-1 .multiselect__tags {
      padding-right: 40px;
    }

    &.actions-2 .multiselect__tags {
      padding-right: 60px;
    }

    &.actions-3 .multiselect__tags {
      padding-right: 80px;
    }

    &.multiselect__tags_white {
      .multiselect__tags {
        background-color: $white;
      }
    }

    &.multiselect {
      width: unset;
      min-width: 150px;
      cursor: pointer;

      .multiselect__select {
        padding: 0;
        width: 30px;
      }

      .multiselect__tags {
        /*padding: 4px 30px 4px 4px;*/
        /*display: flex;*/
        /*align-items: center;*/

        .multiselect__placeholder {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 100%;
          width: 0;
        }

        .multiselect__spinner {
          width: 30px;
          height: 30px;
          background: none;
          top: 50%;
          transform: translateY(-50%);
          right: 2px;

          &:before,
          &:after {
            border-top-color: $vis-primary-color;
          }
        }
      }

      .multiselect__input,
      .multiselect__single {
        background: none;
      }

      .multiselect__single {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 100%;
        width: 0;
      }

      .multiselect__content-wrapper {
        border: 1px solid #CCC;
        // position: fixed;
        // z-index: 1;

        .multiselect__element {
          span.multiselect__option.multiselect__option--highlight:after {
            background: transparent;
            color: $orange;
            font-weight: bold;
          }
        }
      }

      .option {
        white-space: normal;
      }

      &:hover,
      &.multiselect--active {
        .multiselect__tags {
          background-color: #f1f1f1;
        }
      }

      &.multiselect--disabled {
        color: #444;
        background: transparent;
        opacity: 1;
        position: relative;
        text-align: initial;

        .multiselect__select:before {
          border-color: transparent;
        }
        .multiselect__select {
          background: transparent;
        }
      }

      &.loading {
        .multiselect__select {
          display: none;
        }
      }
    }

    .multiselect__option--group {
      font-weight: bold;
      color: #7f7777;

      &:hover {
        background-color: #ededed;
        color: #7f7777;
      }
    }
  }

  .clone_mentions_modal {
    & .multiselect {
      & .clear-select {
        top: auto;
      }
      & .multiselect__tags {
        max-height: 10rem;
        overflow-y: auto;
        line-height: 1.7;
      }
    }
  }
</style>